import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import LoadingOverlay from "../components/LoadingOverlay.jsx";

const API_URL = process.env.REACT_APP_API_URL;

export default function Confirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const order_id = searchParams.get("order_id");

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        if (!order_id) {
          setError("No order ID provided");
          setLoading(false);
          return;
        }

        const res = await fetch(`${API_URL}/api/orders/${order_id}`);
        
        if (!res.ok) {
          throw new Error("Failed to fetch order details");
        }

        const data = await res.json();
        setOrder(data);
      } catch (err) {
        console.error("Error fetching order:", err);
        setError(err.message || "Failed to load order confirmation");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [order_id]);

  const handleAccessBib = () => {
    // Navigate to bib details page
    if (order?.registration_id) {
      navigate(`/registrations/${order.registration_id}`);
    } else if (order_id) {
      navigate(`/orders/${order_id}`);
    }
  };



  if (error) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.card}>
          <div style={styles.errorMessage}>{error}</div>
          <button
            onClick={() => navigate("/events")}
            style={styles.button}
          >
            Retour aux événements
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <div style={styles.card}>
        {/* Success Header */}
        <div style={styles.successHeader}>
          <div style={styles.checkmark}>✓</div>
          <h1 style={styles.title}>Paiement validé !</h1>
          <p style={styles.subtitle}>Votre inscription a été confirmée</p>
        </div>

        {/* Order Details */}
        <div style={styles.detailsSection}>
          <div style={styles.detailRow}>
            <span style={styles.label}>Numéro de commande</span>
            <span style={styles.value}>{order_id}</span>
          </div>

          {order?.race?.name && (
            <div style={styles.detailRow}>
              <span style={styles.label}>Événement</span>
              <span style={styles.value}>{order.race.name}</span>
            </div>
          )}

          {order?.participant_name && (
            <div style={styles.detailRow}>
              <span style={styles.label}>Participant</span>
              <span style={styles.value}>{order.participant_name}</span>
            </div>
          )}

          {order?.amount && (
            <div style={styles.detailRow}>
              <span style={styles.label}>Montant payé</span>
              <span style={styles.value}>€{order.amount.toFixed(2)}</span>
            </div>
          )}

          {order?.created_at && (
            <div style={styles.detailRow}>
              <span style={styles.label}>Date</span>
              <span style={styles.value}>
                {new Date(order.created_at).toLocaleDateString("fr-FR")}
              </span>
            </div>
          )}
        </div>

        {/* Payment Status */}
        <div style={styles.statusBox}>
          <div style={styles.statusBadge}>
            <span style={styles.statusDot}></span>
            Paiement confirmé
          </div>
        </div>

        {/* Call to Action */}
        <div style={styles.actionSection}>
          <p style={styles.actionText}>
            Accédez à votre dossard pour consulter les détails de votre participation
          </p>
          <button
            onClick={handleAccessBib}
            style={styles.primaryButton}
          >
            Accéder au dossard
          </button>
        </div>

        {/* Additional Info */}
        <div style={styles.infoBox}>
          <p style={styles.infoTitle}>Prochaines étapes</p>
          <ul style={styles.infoList}>
            <li>Un email de confirmation a été envoyé à votre adresse</li>
            <li>Consultez votre dossard pour les informations de participation</li>
            <li>Retrouvez tous les détails de l'événement dans votre compte</li>
          </ul>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            onClick={() => navigate("/events")}
            style={styles.secondaryButton}
          >
            Retour aux événements
          </button>
        </div>
        <LoadingOverlay isVisible={loading} message="Chargement de votre confirmation..." />
      </div>
    </div>
  );
}

const styles = {
  pageContainer: {
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 16px rgba(0, 0, 0, 0.08)",
    maxWidth: "600px",
    width: "100%",
    padding: "40px",
  },
  successHeader: {
    textAlign: "center",
    marginBottom: "32px",
  },
  checkmark: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    backgroundColor: "#d4edda",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    color: "#28a745",
    margin: "0 auto 16px",
    fontWeight: "700",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1a1a1a",
    margin: "0 0 8px 0",
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    margin: "0",
  },
  detailsSection: {
    backgroundColor: "#f9fafb",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "24px",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: "12px",
    marginBottom: "12px",
    borderBottom: "1px solid #e5e7eb",
  },
  detailRow_last: {
    borderBottom: "none",
    marginBottom: "0",
    paddingBottom: "0",
  },
  label: {
    fontSize: "14px",
    color: "#666",
    fontWeight: "500",
  },
  value: {
    fontSize: "14px",
    color: "#1a1a1a",
    fontWeight: "600",
  },
  statusBox: {
    backgroundColor: "#d4edda",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "24px",
    textAlign: "center",
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    color: "#28a745",
    fontWeight: "600",
    fontSize: "14px",
  },
  statusDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#28a745",
    display: "inline-block",
  },
  actionSection: {
    marginBottom: "24px",
    textAlign: "center",
  },
  actionText: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "16px",
    margin: "0 0 16px 0",
  },
  primaryButton: {
    width: "100%",
    padding: "12px 24px",
    backgroundColor: "#0070f0",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  infoBox: {
    backgroundColor: "#f0f7ff",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "24px",
    borderLeft: "4px solid #0070f0",
  },
  infoTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1a1a1a",
    margin: "0 0 12px 0",
  },
  infoList: {
    fontSize: "13px",
    color: "#666",
    margin: "0",
    paddingLeft: "20px",
  },
  footer: {
    display: "flex",
    gap: "12px",
  },
  secondaryButton: {
    flex: "1",
    padding: "12px 24px",
    backgroundColor: "#f0f0f0",
    color: "#1a1a1a",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  button: {
    width: "100%",
    padding: "12px 24px",
    backgroundColor: "#0070f0",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "16px",
  },
  errorMessage: {
    padding: "12px",
    backgroundColor: "#fee",
    color: "#c33",
    borderRadius: "6px",
    marginBottom: "16px",
    fontSize: "14px",
    border: "1px solid #fcc",
  },
  loadingText: {
    textAlign: "center",
    fontSize: "16px",
    color: "#666",
    padding: "40px 20px",
  },
};
